const SCH_NET_OVERLAY_ID = 'selected-sch-net';
// Paintable SVG shape tags whose stroke/fill the net highlight recolours.
const SCH_NET_SHAPE_SELECTOR = 'path,line,polyline,polygon,rect,circle,ellipse,text';
// Fallback for --color-text (defined in themes/theme-gitea-*.css) when the computed var is empty, e.g. tests.
const THEME_TEXT_FALLBACK = '#181c21';

// True if any highlight overlay is present, so we don't restore opacity while one is still active.
function hasActiveOverlay(svg: SVGSVGElement): boolean {
  return Boolean(svg.querySelector(`#${SCH_NET_OVERLAY_ID}, #selected-net, #selected-component, #selected-net-from, #selected-net-to, #selected-component-from, #selected-component-to`));
}

export class SearchableElementBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;

  /**
   * Clamp possible bounding boxes to the dimensions of the svg
   */
  constructor(svgBounds: DOMRect) {
    this.minX = svgBounds.right;
    this.minY = svgBounds.bottom;
    this.maxX = svgBounds.left;
    this.maxY = svgBounds.top;
  }

  /**
   * Tighten the bounds based on some rect contained within the bounds
   * @param {DOMRect} elemBounds
   */
  updateBounds(elemBounds: DOMRect) {
    // only consider elements with non-zero size (lines may have 0 in one dimension)
    if (elemBounds.width > 0 || elemBounds.height > 0) {
      this.minX = Math.min(elemBounds.left, this.minX);
      this.minY = Math.min(elemBounds.top, this.minY);
      this.maxX = Math.max(elemBounds.right, this.maxX);
      this.maxY = Math.max(elemBounds.bottom, this.maxY);
    }
  }
}

/**
 * Copy elements from a layer into a new group
 * @param {Element} currentLayer
 * @param {string} dataSelector a CSS selector indicating the data selector to look for
 * @returns {SVGGElement}
 */
function copyElementByLayer(currentLayer: Element, dataSelector: string) {
  const layerGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  layerGroup.setAttribute('data-layer-id', currentLayer.getAttribute('data-layer-id'));
  layerGroup.setAttribute('color', currentLayer.getAttribute('color'));
  layerGroup.setAttribute('fill', currentLayer.getAttribute('fill'));
  for (const diffElem of currentLayer.querySelectorAll(dataSelector)) {
    layerGroup.append(diffElem.cloneNode(true));
  }
  return layerGroup;
}

/**
 * Toggle net highlight on or off
 * @param {SVGSVGElement} svg the svg in which to show the element
 * @param {boolean} showHighlight whether to toggle the highlight on
 * @param {string} dataSelector a CSS selector indicating the data selector to look for
 * @param {string} elementType the type of the element being selected (i.e. net, component)
 */
function internalPcbToggleHighlightElement(svg: SVGSVGElement, showHighlight: boolean, dataSelector: string, elementType: string) {
  if (showHighlight) {
    const foregroundEls: NodeListOf<SVGElement> = svg.querySelectorAll(`[data-group="foreground"]`);
    for (const fg of foregroundEls) {
      fg.style.opacity = '0.3';
    }

    const diffEls: NodeListOf<SVGElement> = svg.querySelectorAll('g.diff');
    for (const diff of diffEls) {
      diff.style.opacity = '0.3';
    }

    const elementFromGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    const elementToGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    const elementGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    elementFromGroup.setAttribute('id', `selected-${elementType}-from`);
    elementFromGroup.setAttribute('class', 'diff diff_from');
    elementFromGroup.style.strokeLinejoin = 'round';
    elementFromGroup.style.color = '#DB454E';
    elementToGroup.setAttribute('id', `selected-${elementType}-to`);
    elementToGroup.setAttribute('class', 'diff diff_to');
    elementToGroup.style.strokeLinejoin = 'round';
    elementToGroup.style.color = '#259870';
    elementGroup.setAttribute('id', `selected-${elementType}`);
    elementGroup.style.strokeLinejoin = 'round';

    for (const visibleLayer of svg.querySelectorAll(`g.diff_from g[data-layer-id]:not(.layer-hidden)`)) {
      elementFromGroup.append(copyElementByLayer(visibleLayer, dataSelector));
    }

    for (const visibleLayer of svg.querySelectorAll(`g.diff_to g[data-layer-id]:not(.layer-hidden)`)) {
      elementToGroup.append(copyElementByLayer(visibleLayer, dataSelector));
    }

    for (const visibleLayer of svg.querySelectorAll(`g[data-group="foreground"] g[data-layer-id]:not(.layer-hidden)`)) {
      elementGroup.append(copyElementByLayer(visibleLayer, dataSelector));
    }

    svg.append(elementGroup);
    svg.append(elementFromGroup);
    svg.append(elementToGroup);
  } else {
    // Remove overlays first so hasActiveOverlay doesn't find our own.
    svg.querySelector(`#selected-${elementType}-from`)?.remove();
    svg.querySelector(`#selected-${elementType}-to`)?.remove();
    svg.querySelector(`#selected-${elementType}`)?.remove();

    if (!hasActiveOverlay(svg)) {
      const foregroundEls: NodeListOf<SVGElement> = svg.querySelectorAll(`[data-group="foreground"]`);
      for (const fg of foregroundEls) {
        fg.style.opacity = '';
      }

      const diffEls: NodeListOf<SVGElement> = svg.querySelectorAll('g.diff');
      for (const diff of diffEls) {
        diff.style.opacity = '';
      }
    }
  }
}

/**
 * Toggle element highlight on or off
 * @param {SVGSVGElement} svg the svg in which to show the element
 * @param {string} dataSelector a CSS selector indicating data selector to isolate element
 * @param {boolean} showHighlight whether to toggle the highlight on
 * @param {string} elementTypeName the name of the element type being selected (i.e. component, net)
 * @param {boolean} isDiff
 * @param {boolean} isPcb
 */
const SCH_HIGHLIGHT_CLASS = 'sch-search-highlight';
// Unique filter id per svg: WebKit won't resolve filter="url(#id)" across <svg>s, so a shared id breaks the glow on multi-page DRs.
const SCH_HIGHLIGHT_FILTER_ID_PREFIX = 'sch-search-highlight-shadow';
let schHighlightFilterSeq = 0;
const SCH_HIGHLIGHT_FALLBACK_COLOR = '#4183c4'; // --color-primary, if the CSS var can't be read
// Glow halo is stacked because the diff's grayblur desaturates it; a single pass washes out.
const SCH_GLOW_STACK_PASSES = 3;

// WebKit doesn't render CSS filter:drop-shadow on SVG, so the glow is an SVG-native filter (the
// `filter` attribute): flood the blurred silhouette with the primary colour, stacked to survive the
// diff's grayblur desaturation. Injected once per SVG.
function ensureSchHighlightFilter(svg: SVGSVGElement): string {
  const ns = 'http://www.w3.org/2000/svg';
  const primary = getComputedStyle(svg).getPropertyValue('--color-primary').trim() || SCH_HIGHLIGHT_FALLBACK_COLOR;
  let filter = svg.querySelector(`filter[id^="${SCH_HIGHLIGHT_FILTER_ID_PREFIX}"]`);
  if (!filter) {
    // Blur radius in user units, scaled to the sheet size and clamped to a 1-unit minimum.
    const base = svg.viewBox?.baseVal;
    const stdDeviation = Math.max(Math.max(base?.width ?? 0, base?.height ?? 0) * 0.004, 1);
    let defs = svg.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS(ns, 'defs');
      svg.prepend(defs);
    }
    filter = document.createElementNS(ns, 'filter');
    // Unique id per svg (see the prefix constant for why WebKit needs this).
    filter.setAttribute('id', `${SCH_HIGHLIGHT_FILTER_ID_PREFIX}-${++schHighlightFilterSeq}`);
    // Roomy region so the blur isn't clipped to the element's bounding box.
    filter.setAttribute('x', '-50%');
    filter.setAttribute('y', '-50%');
    filter.setAttribute('width', '200%');
    filter.setAttribute('height', '200%');
    // Blur the silhouette, flood with primary, merge the halo SCH_GLOW_STACK_PASSES times.
    const blur = document.createElementNS(ns, 'feGaussianBlur');
    blur.setAttribute('in', 'SourceAlpha');
    blur.setAttribute('stdDeviation', String(stdDeviation));
    blur.setAttribute('result', 'glowBlur');
    const flood = document.createElementNS(ns, 'feFlood');
    flood.setAttribute('flood-opacity', '1');
    flood.setAttribute('result', 'glowFlood');
    const composite = document.createElementNS(ns, 'feComposite');
    composite.setAttribute('in', 'glowFlood');
    composite.setAttribute('in2', 'glowBlur');
    composite.setAttribute('operator', 'in');
    composite.setAttribute('result', 'glow');
    const merge = document.createElementNS(ns, 'feMerge');
    for (let i = 0; i < SCH_GLOW_STACK_PASSES; i++) {
      const node = document.createElementNS(ns, 'feMergeNode');
      node.setAttribute('in', 'glow');
      merge.append(node);
    }
    const sourceNode = document.createElementNS(ns, 'feMergeNode');
    sourceNode.setAttribute('in', 'SourceGraphic');
    merge.append(sourceNode);
    filter.append(blur, flood, composite, merge);
    defs.append(filter);
  }
  // Keep the glow in sync with the (theme-dependent) primary colour on each highlight.
  filter.querySelector('feFlood')?.setAttribute('flood-color', primary);
  return filter.id;
}

function internalSchToggleHighlightElement(svg: SVGSVGElement, showHighlight: boolean, dataSelector: string) {
  if (showHighlight) {
    // Reference this svg's own filter so the url(#id) resolves within the same <svg> (WebKit).
    const filterRef = `url(#${ensureSchHighlightFilter(svg)})`;
    for (const elem of svg.querySelectorAll<SVGElement>(dataSelector)) {
      elem.classList.add(SCH_HIGHLIGHT_CLASS);
      elem.setAttribute('filter', filterRef);
    }
  } else {
    const existing = svg.querySelector(`filter[id^="${SCH_HIGHLIGHT_FILTER_ID_PREFIX}"]`);
    const filterRef = existing ? `url(#${existing.id})` : null;
    for (const elem of svg.querySelectorAll<SVGElement>(`.${SCH_HIGHLIGHT_CLASS}`)) {
      elem.classList.remove(SCH_HIGHLIGHT_CLASS);
      // Only clear the filter if it's ours (don't stomp a filter the element already had).
      if (filterRef && elem.getAttribute('filter') === filterRef) {
        elem.removeAttribute('filter');
      }
    }
  }
}

function internalSchNetToggleHighlightElement(svg: SVGSVGElement, showHighlight: boolean, dataSelector: string) {
  if (showHighlight) {
    // Dim all SVG content
    for (const child of svg.children) {
      if (child instanceof SVGElement && child.id !== SCH_NET_OVERLAY_ID) {
        child.style.opacity = '0.3';
      }
    }

    // Clone matched elements into a full-opacity overlay, preserving ancestor transforms so
    // clones render at the correct position.
    const overlay = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    overlay.setAttribute('id', SCH_NET_OVERLAY_ID);
    const sources = Array.from(svg.querySelectorAll(dataSelector));
    for (const elem of sources) {
      const clone = elem.cloneNode(true) as SVGElement;

      // Wrap the clone in a matching <g> for each transformed ancestor up to the SVG root.
      let wrapper: SVGElement = clone;
      let ancestor: Element | null = elem.parentElement;
      while (ancestor && ancestor !== svg) {
        const transform = ancestor.getAttribute('transform');
        if (transform) {
          const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          g.setAttribute('transform', transform);
          g.append(wrapper);
          wrapper = g;
        }
        ancestor = ancestor.parentElement;
      }

      overlay.append(wrapper);
    }
    svg.append(overlay);

    // Colour each clone by diff status — added/removed/changed keep their colour, unchanged uses
    // the theme foreground so it shows on the dimmed sheet in either theme.  Read from the source.
    const unchangedColor = getComputedStyle(svg).getPropertyValue('--color-text').trim() || THEME_TEXT_FALLBACK;
    // The element itself (if paintable) plus its paintable descendants.
    const collectShapes = (root: Element): SVGElement[] => {
      const shapes = Array.from(root.querySelectorAll<SVGElement>(SCH_NET_SHAPE_SELECTOR));
      if (root.matches(SCH_NET_SHAPE_SELECTOR)) shapes.unshift(root as SVGElement);
      return shapes;
    };
    for (const [i, src] of sources.entries()) {
      const wrapper = overlay.children.item(i);
      if (!wrapper) continue;
      const isUnchanged = !src.closest('.diff_to') && !src.closest('.diff_from');
      const dstShapes = collectShapes(wrapper);
      for (const [j, srcShape] of collectShapes(src).entries()) {
        const dstShape = dstShapes[j];
        if (!dstShape) continue;
        const cs = getComputedStyle(srcShape);
        if (cs.stroke !== 'none') dstShape.style.stroke = isUnchanged ? unchangedColor : cs.stroke;
        if (cs.fill !== 'none' && cs.fill !== 'rgba(0, 0, 0, 0)') dstShape.style.fill = isUnchanged ? unchangedColor : cs.fill;
      }
    }
  } else {
    // Remove overlay
    for (const el of svg.querySelectorAll(`#${SCH_NET_OVERLAY_ID}`)) el.remove();

    // Restore opacity only if no other highlight overlay is active.
    if (!hasActiveOverlay(svg)) {
      for (const child of svg.children) {
        if (child instanceof SVGElement) {
          child.style.opacity = '';
        }
      }
    }
  }
}

function toggleHighlightElement(svg: SVGSVGElement | null, dataSelector: string, showHighlight: boolean, elementTypeName: string, isPcb: boolean) {
  if (isPcb) {
    if (svg) {
      internalPcbToggleHighlightElement(
        svg,
        showHighlight,
        dataSelector,
        elementTypeName,
      );
    }
  } else if (elementTypeName === 'net') {
    if (svg) {
      internalSchNetToggleHighlightElement(svg, showHighlight, dataSelector);
    }
  } else if (svg) {
    internalSchToggleHighlightElement(svg, showHighlight, dataSelector);
  }
}

/**
 * Get the bounds of an svg element group (by data selector)
 */
function getElementBounds(svg: SVGSVGElement, dataSelector: string): SearchableElementBounds {
  const svgRect = svg.getBoundingClientRect();
  const elementBounds = new SearchableElementBounds(svgRect);
  for (const elem of svg.querySelectorAll(dataSelector)) {
    const rect = elem.getBoundingClientRect();
    // Skip elements entirely outside the SVG viewport (e.g. diff base overlay)
    if (rect.bottom < svgRect.top || rect.top > svgRect.bottom ||
        rect.right < svgRect.left || rect.left > svgRect.right) {
      continue;
    }
    elementBounds.updateBounds(rect);
  }
  return elementBounds;
}

export {toggleHighlightElement, getElementBounds};
